/**
 * Lydaas H5 chat opened from the floating rail.
 * form_data is compact JSON, encoded once, then appended with &.
 */

const LYDAAS_CHAT_LAUNCHER = '[data-lydaas-chat-launcher]';

/**
 * @param {string} baseUrl
 * @param {string | null | undefined} email
 * @param {string | number | null | undefined} userId
 * @param {string | null | undefined} tags Comma-separated customer tags. The form field is tags, empty when there are none.
 * @returns {string}
 */
function buildLydaasChatUrl(baseUrl, email, userId, tags) {
  const id = userId == null ? '' : String(userId);
  if (!baseUrl || id === '') {
    return '';
  }

  const mail = email == null || String(email) === '' ? id : String(email);
  const form = { email: mail, userId: id, tags: tags == null ? '' : String(tags) };
  return `${baseUrl}&form_data=${encodeURIComponent(JSON.stringify(form))}`;
}

window.buildLydaasChatUrl = buildLydaasChatUrl;

class LydaasChat extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    window.LydaasChat = {
      open: () => this.open(),
      close: () => this.close(),
      toggle: () => this.toggle(),
      buildUrl: (email, userId, tags) => buildLydaasChatUrl(this.dataset.baseUrl, email, userId, tags),
    };

    this._onDocumentClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const launcher = target ? target.closest(LYDAAS_CHAT_LAUNCHER) : null;
      if (!launcher || this.dataset.loggedIn !== 'true') return;
      event.preventDefault();
      this.toggle();
    };

    this._onKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    };

    this._onBackdropTouchMove = (event) => {
      if (!this.classList.contains('is-open') || !this._isMobile()) return;
      event.preventDefault();
    };
    this.querySelector('[data-lydaas-chat-backdrop]')?.addEventListener('touchmove', this._onBackdropTouchMove, { passive: false });

    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('keydown', this._onKeydown);
    this._onViewport = () => this._fitViewport();
    window.visualViewport?.addEventListener('resize', this._onViewport);
    window.visualViewport?.addEventListener('scroll', this._onViewport);
  }

  disconnectedCallback() {
    this._mounted = false;
    this._unlockPage();
    this.querySelector('[data-lydaas-chat-backdrop]')?.removeEventListener('touchmove', this._onBackdropTouchMove);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keydown', this._onKeydown);
    window.visualViewport?.removeEventListener('resize', this._onViewport);
    window.visualViewport?.removeEventListener('scroll', this._onViewport);
    if (window.LydaasChat && window.LydaasChat.open) {
      delete window.LydaasChat;
    }
  }

  /**
   * @returns {boolean}
   */
  open() {
    if (this.dataset.loggedIn !== 'true') return false;

    const url = buildLydaasChatUrl(this.dataset.baseUrl, this.dataset.email, this.dataset.userId, this.dataset.customerTags);
    if (!url) return false;

    const frame = this.querySelector('[data-lydaas-chat-frame]');
    if (frame instanceof HTMLIFrameElement && !frame.getAttribute('src')) {
      frame.setAttribute('src', url);
    }

    this.hidden = false;
    this.classList.add('is-open');
    this._lockPage();
    this._fitViewport();
    this._syncLaunchers(true);
    return true;
  }

  close() {
    this.classList.remove('is-open');
    this.hidden = true;
    this._unlockPage();
    this._fitViewport();
    this._syncLaunchers(false);
  }

  /**
   * @returns {boolean}
   */
  _isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  /**
   * Pin the page without overflow:hidden. iOS drops taps inside a cross-origin
   * frame when any ancestor clips overflow, so the lock is position only.
   */
  _lockPage() {
    if (this._pageLocked || !this._isMobile()) return;
    this._pageLocked = true;
    this._scrollY = window.scrollY;
    document.documentElement.classList.add('lydaas-chat-lock');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  _unlockPage() {
    if (!this._pageLocked) return;
    this._pageLocked = false;
    document.documentElement.classList.remove('lydaas-chat-lock');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, this._scrollY || 0);
  }

  /**
   * Keep the frame above the mobile keyboard. A fixed panel that stays under
   * the keyboard makes iOS drop focus inside the cross-origin frame.
   */
  _fitViewport() {
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const viewport = window.visualViewport;
    const covered = viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0;
    if (!this.classList.contains('is-open') || !mobile || covered < 80) {
      this.style.removeProperty('height');
      this.style.removeProperty('bottom');
      return;
    }

    const edge = 12;
    const available = viewport.height - edge;
    const height = Math.max(240, Math.min(640, available));
    this.style.bottom = `${Math.round(covered + edge)}px`;
    this.style.height = `${Math.round(height)}px`;
  }

  toggle() {
    if (this.classList.contains('is-open')) {
      this.close();
      return;
    }
    this.open();
  }

  /**
   * @param {boolean} open
   */
  _syncLaunchers(open) {
    document.querySelectorAll(LYDAAS_CHAT_LAUNCHER).forEach((button) => {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      button.querySelector('[data-lydaas-chat-icon="open"]')?.toggleAttribute('hidden', open);
      button.querySelector('[data-lydaas-chat-icon="close"]')?.toggleAttribute('hidden', !open);
    });
  }
}

if (!customElements.get('lydaas-chat')) {
  customElements.define('lydaas-chat', LydaasChat);
}
